import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import styled from 'styled-components';
import Auth from './components/Auth';
import DraftsPageContent from './components/DraftsPageContent';
import { DraftProvider } from './contexts/DraftContext';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import LandingPage from './pages/LandingPage';
import AdminPage from './pages/AdminPage';
import DraftSimulator from './pages/DraftSimulator';
import PrivateRoute from './components/PrivateRoute';
import { AuthProvider } from './contexts/AuthContext';
import firebaseService from './services/FirebaseService';

// Create a dark theme
const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#90caf9',
    },
    secondary: {
      main: '#f48fb1',
    },
    background: {
      default: '#121212',
      paper: '#1e1e1e',
    },
  },
  typography: {
    fontFamily: '"Orbitron", monospace',
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          fontWeight: 'bold',
        },
      },
    },
  },
});

const AppContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background-color: #121212;
  color: #fff;
`;

const Header = styled.header`
  background-color: #1a1a1a;
  padding: 16px 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Logo = styled.h1`
  margin: 0;
  font-size: 24px;
  color: #e94560;
`;

const MainContent = styled.main`
  flex: 1;
  padding: 24px 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100vw;
  overflow-x: auto;
`;

// Admin route component - checks if user is an admin
const AdminRoute = ({ children }) => {
  const [isAdmin, setIsAdmin] = React.useState(false);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    const checkAdminAccess = async () => {
      try {
        const user = firebaseService.getCurrentUser();
        if (user) {
          const adminStatus = await firebaseService.isUserAdmin(user.uid);
          setIsAdmin(adminStatus);
        }
        setLoading(false);
      } catch (error) {
        console.error('Error checking admin access:', error);
        setLoading(false);
      }
    };
    
    checkAdminAccess();
  }, []);

  if (loading) {
    return null; // or a loading spinner
  }
  
  if (!isAdmin) {
    return <Navigate to="/drafts" replace />;
  }
  
  return children;
};

function App() {
  return (
    <AuthProvider>
      <DraftProvider>
        <Router>
          <ThemeProvider theme={darkTheme}>
            <CssBaseline />
            <Routes>
              {/* Public Routes */}
              <Route path="/" element={<LandingPage />} />
              
              {/* Protected Routes */}
              <Route element={<PrivateRoute />}>
                {/* Main Dashboard */}
                <Route 
                  path="/drafts" 
                  element={
                    <AppContainer>
                      <Header>
                        <Logo>Dota 2 Draft Planner/Simulator</Logo>
                        <Auth />
                      </Header>
                      <MainContent>
                        <DraftsPageContent />
                      </MainContent>
                    </AppContainer>
                  } 
                />
                
                {/* Draft Simulator with Match Data Pull */}
                <Route 
                  path="/simulator" 
                  element={<DraftSimulator />} 
                />
                <Route 
                  path="/simulator/:draftId" 
                  element={<DraftSimulator />} 
                />
                
                {/* Admin Page - Additional admin check */}
                <Route 
                  path="/admin" 
                  element={
                    <AdminRoute>
                      <AppContainer>
                        <Header>
                          <Logo>Dota 2 Draft Planner/Simulator</Logo>
                          <Auth />
                        </Header>
                        <MainContent>
                          <AdminPage />
                        </MainContent>
                      </AppContainer>
                    </AdminRoute>
                  } 
                />
              </Route>
              
              {/* Fallback route - redirect all other routes to home page */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </ThemeProvider>
        </Router>
      </DraftProvider>
    </AuthProvider>
  );
}

export default App;